import React from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';

// Draft/preview bubble that lives INSIDE the WhatsApp conversation (on the chat
// background, right-aligned like a pending outgoing message) while the Personal
// Reply composer is active. It mirrors the LIVE composed message (greeting +
// typed message + sign-off), styled per personal-reply-mock.html: outgoing green
// with a pulsing green glow + a "Draft" tag pill, and NO timestamp/ticks.
//
// The `preview` payload is produced by usePersonalReply (surfaced by ChatPage),
// split by splitQuickReplyPreview so name/{{time_of_day}}/answer already resolve
// — time_of_day is preview-only.

const DRAFT = {
    out: '#dcf8c6',
    ink: '#111b21',
    msg: '#111111',
    placeholder: '#88aa99',
    tagBg: '#00a884',
    glowRest: '0 0 0 2px #25d36688, 0 0 14px 2px #25d36666',
};

const msgSx = (hasAnswer) => ({
    display: 'block',
    m: '4px 0',
    p: '2px 0',
    color: hasAnswer ? DRAFT.msg : DRAFT.placeholder,
    fontWeight: hasAnswer ? 600 : 400,
    fontStyle: hasAnswer ? 'normal' : 'italic',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
});

function QuickReplyDraftBubble({ preview }) {
    if (!preview) return null;
    const { before, answer, after, hasAnswer, hasAnswerSlot } = preview;

    return (
        <Box
            aria-label="Draft reply preview"
            sx={{
                position: 'relative',
                alignSelf: 'flex-end',
                maxWidth: '80%',
                minWidth: 120,
                mx: '12px',
                my: '4px',
                padding: '7px 10px 5px',
                fontSize: '13.5px',
                lineHeight: 1.4,
                color: DRAFT.ink,
                backgroundColor: DRAFT.out,
                borderRadius: '9px',
                borderTopRightRadius: '2px',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                // Pulsing green glow — clearly a live draft. Static when the user
                // prefers reduced motion.
                '@keyframes qrDraftGlow': {
                    '0%, 100%': { boxShadow: '0 0 0 2px #25d36688, 0 0 10px 1px #25d36655' },
                    '50%': { boxShadow: '0 0 0 2px #25d366cc, 0 0 18px 4px #25d36699' },
                },
                animation: 'qrDraftGlow 2.2s ease-in-out infinite',
                '@media (prefers-reduced-motion: reduce)': {
                    animation: 'none',
                    boxShadow: DRAFT.glowRest,
                },
            }}
        >
            {/* "Draft" tag pill — top-right corner. */}
            <Box
                component="span"
                sx={{
                    position: 'absolute',
                    top: '-9px',
                    right: '8px',
                    px: '7px',
                    py: '1px',
                    borderRadius: '8px',
                    fontSize: '9.5px',
                    fontWeight: 700,
                    letterSpacing: '0.3px',
                    color: '#FFFFFF',
                    backgroundColor: DRAFT.tagBg,
                }}
            >
                Draft
            </Box>

            {hasAnswerSlot ? (
                <>
                    {before ? before.trimEnd() : null}
                    <Box component="span" sx={msgSx(hasAnswer)}>
                        {hasAnswer ? answer : '↳ Type your personalised message — it\'ll appear here'}
                    </Box>
                    {after ? after.trimStart() : null}
                </>
            ) : (
                before
            )}
        </Box>
    );
}

QuickReplyDraftBubble.propTypes = {
    // Null when the composer is inactive / has no resolved template → renders nothing.
    preview: PropTypes.shape({
        before: PropTypes.string,
        answer: PropTypes.string,
        after: PropTypes.string,
        hasAnswer: PropTypes.bool,
        hasAnswerSlot: PropTypes.bool,
    }),
};

QuickReplyDraftBubble.defaultProps = {
    preview: null,
};

export default QuickReplyDraftBubble;
